import type { FunctionComponent, ReactNode, SVGProps } from 'react';
import classNames from 'classnames';

import { Badge, type BadgeTone } from '@/components/atoms/Badge';
import { Card } from '@/components/atoms/Card';
import { Caption } from '@/components/atoms/Eyebrow';
import SkeletonBox from '@/components/atoms/SkeletonBox/SkeletonBox';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import TitleIcon from '@/components/atoms/TitleIcon/TitleIcon';

import styles from './MetricCard.module.scss';

const SKELETON_WIDTH = 100;
const SKELETON_HEIGHT = 26;
const SKELETON_RADIUS = 8;

export type MetricCardIcon = FunctionComponent<SVGProps<SVGSVGElement>>;

/**
 * The colour family of the icon halo or a badge, resolved to theme tokens in the stylesheet:
 * `success` green, `info` blue, `neutral` lavender, `caution` yellow, `warning` orange,
 * `danger` red. A tone names a colour, not a verdict: living weight is `caution` yellow on
 * every page because that is its colour, not because it is a warning.
 */
export type MetricTone = 'success' | 'info' | 'neutral' | 'caution' | 'warning' | 'danger';

/**
 * Pill rendered next to the value. The caller owns the copy and picks the tone, so this
 * component carries no domain thresholds and no namespace of its own: a page that
 * classifies a metric maps its own verdict to a badge.
 */
export interface MetricCardBadge {
    label: string;
    tone: MetricTone;
    icon?: MetricCardIcon;
}

export interface MetricCardProps {
    /** Uppercase above the value in the `inline` layout; under the icon in `tile`. */
    title: string;
    value: string | number | ReactNode;
    icon: MetricCardIcon;
    /** Colour of the icon and its halo. */
    tone: MetricTone;
    /**
     * `inline`: icon left of title and value, the dashboards' summary row.
     * `tile`: a fixed-height bordered tile with everything centred, for report summaries.
     */
    layout?: 'inline' | 'tile';
    /**
     * Inline layout only: sets the metric in a card. `tinted` is the pale tile headline metrics
     * use inside another card; `surface` is a white card of its own on the page background.
     */
    framed?: 'tinted' | 'surface';
    /** Replaces the value with a skeleton box. */
    isLoading?: boolean;
    /** Overrides the default 24px icon. */
    iconCustomSize?: number;
    /** Trails the value in a smaller font, e.g. a unit or `of measured fish`. */
    suffix?: string;
    /** Sits right of the value, e.g. a trend arrow. */
    trend?: ReactNode;
    badge?: MetricCardBadge;
    className?: string;
    /**
     * Applied to the `<svg>` itself. Only needed by consumers whose icon set requires a
     * per-path override; the colour comes from `tone`.
     */
    iconClassName?: string;
}

// Badge has no separate yellow: its warning is MetricCard's caution, and its elevated orange
// is MetricCard's warning.
const BADGE_TONE: Record<MetricTone, BadgeTone> = {
    success: 'success',
    info: 'info',
    neutral: 'neutral',
    caution: 'warning',
    warning: 'elevated',
    danger: 'danger',
};

function MetricBadge(props: { badge: MetricCardBadge }) {
    const { badge } = props;
    return (
        <Badge
            tone={BADGE_TONE[badge.tone]}
            icon={badge.icon && <SvgIcon icon={badge.icon} size="small" />}>
            {badge.label}
        </Badge>
    );
}

/**
 * Icon, title and value: the visual base for every metric summary card in the portal.
 *
 * Deliberately free of domain knowledge: icon, tone and the optional badge all arrive as
 * props, so module cards (`BehaviorMetricCard`, `HealthWoundsMetricCard`) wrap this rather
 * than copying it.
 */
export default function MetricCard(props: MetricCardProps) {
    const {
        title,
        value,
        icon,
        tone,
        layout = 'inline',
        framed,
        isLoading = false,
        iconCustomSize,
        suffix,
        trend,
        badge,
        className,
        iconClassName,
    } = props;

    const iconHalo = (
        <TitleIcon
            icon={icon}
            tone={tone}
            iconSize={iconCustomSize || 24}
            iconClassName={iconClassName}
            className={layout === 'tile' ? styles.tileIcon : undefined}
        />
    );

    if (layout === 'tile') {
        return (
            <Card
                variant="outlined"
                fullWidth={false}
                className={classNames(styles.tile, className)}>
                {iconHalo}
                <div className={styles.tileTitle}>{title}</div>
                <div className={styles.tileValue}>{value}</div>
            </Card>
        );
    }

    const content = (
        <>
            {iconHalo}
            <div className={styles.content}>
                <Caption as="div">{title}</Caption>
                {isLoading ? (
                    <SkeletonBox
                        width={SKELETON_WIDTH}
                        height={SKELETON_HEIGHT}
                        borderRadius={SKELETON_RADIUS}
                    />
                ) : (
                    <div className={styles.value}>
                        {value}
                        {trend}
                        {badge && <MetricBadge badge={badge} />}
                        {suffix && <div className={styles.suffix}>{suffix}</div>}
                    </div>
                )}
            </div>
        </>
    );

    if (framed) {
        return (
            <Card
                variant={framed === 'tinted' ? 'tinted' : 'surface'}
                className={classNames(styles.container, styles.framed, className)}>
                {content}
            </Card>
        );
    }

    return <div className={classNames(styles.container, className)}>{content}</div>;
}
